import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

// Tables aren't in generated types yet — use an untyped client.
const sb = () => supabaseAdmin as unknown as { from: (t: string) => any };

export type ArticleCategory = {
  id: string;
  slug: string;
  parent_id: string | null;
  icon: string | null;
  sort: number;
  name: string;
  description: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  article_count?: number;
};

export type ArticleSummary = {
  id: string;
  slug: string;
  category_id: string;
  cover_image_url: string | null;
  author_name: string | null;
  read_minutes: number | null;
  published_at: string;
  title: string;
  excerpt: string | null;
};

export const DEFAULT_LOCALE = "en";
export const ARTICLES_LOCALES = ["en", "vi"] as const;

function pickTr(rows: any[] | undefined, locale: string): any {
  if (!rows || rows.length === 0) return undefined;
  return rows.find((t: any) => t.locale === locale)
    ?? rows.find((t: any) => t.locale === DEFAULT_LOCALE)
    ?? rows[0];
}

export function articlesHrefBase(locale: string): string {
  return locale === DEFAULT_LOCALE ? "/articles" : `/vi/articles`;
}

export function buildArticlesHref(locale: string, segments: string[]): string {
  const base = articlesHrefBase(locale);
  if (segments.length === 0) return base;
  return `${base}/${segments.join("/")}`;
}

/** Full articles tree (categories + light article list) for hub + category pages. */
export const getArticlesTree = createServerFn({ method: "GET" })
  .inputValidator((d: { locale?: string } | undefined) =>
    z.object({ locale: z.string().min(2).max(8).optional() }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const locale = data.locale ?? DEFAULT_LOCALE;
    try {
      const [cats, arts] = await Promise.all([
        sb()
          .from("article_categories")
          .select("id, slug, parent_id, icon, sort, is_published, article_category_translations(name, description, seo_title, seo_description, locale)")
          .eq("is_published", true)
          .order("sort"),
        sb()
          .from("articles")
          .select("id, slug, category_id, cover_image_url, author_name, read_minutes, published_at, is_published, sort, article_translations(title, excerpt, locale)")
          .eq("is_published", true)
          .order("published_at", { ascending: false }),
      ]);

      const categories: ArticleCategory[] = (cats.data ?? []).map((c: any) => {
        const tr = pickTr(c.article_category_translations, locale);
        return {
          id: c.id, slug: c.slug, parent_id: c.parent_id, icon: c.icon, sort: c.sort,
          name: tr?.name ?? c.slug,
          description: tr?.description ?? null,
          seo_title: tr?.seo_title ?? null,
          seo_description: tr?.seo_description ?? null,
          article_count: 0,
        };
      });

      const articles: ArticleSummary[] = (arts.data ?? []).map((a: any) => {
        const tr = pickTr(a.article_translations, locale);
        return {
          id: a.id, slug: a.slug, category_id: a.category_id,
          cover_image_url: a.cover_image_url,
          author_name: a.author_name,
          read_minutes: a.read_minutes,
          published_at: a.published_at,
          title: tr?.title ?? a.slug,
          excerpt: tr?.excerpt ?? null,
        };
      });

      // Article counts, bubbled to ancestors.
      const counts = new Map<string, number>();
      for (const a of articles) counts.set(a.category_id, (counts.get(a.category_id) ?? 0) + 1);
      const byId = new Map(categories.map((c) => [c.id, c]));
      for (const c of categories) {
        let cur: ArticleCategory | undefined = c;
        const own = counts.get(c.id) ?? 0;
        while (cur) {
          cur.article_count = (cur.article_count ?? 0) + own;
          cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
        }
      }

      return { categories, articles };
    } catch (e) {
      console.error("[articles] getArticlesTree failed", e);
      return { categories: [] as ArticleCategory[], articles: [] as ArticleSummary[] };
    }
  });

export type ArticlePathResult =
  | { type: "none" }
  | { type: "category"; locale: string;
      breadcrumbs: { slug: string; name: string }[];
      category: { id: string; slug: string; name: string; description: string | null; icon: string | null; seo_title: string | null; seo_description: string | null };
      subcategories: { id: string; slug: string; name: string; description: string | null; icon: string | null; article_count: number }[];
      articles: ArticleSummary[];
      canonicalPathByLocale: Record<string, string>;
    }
  | { type: "article"; locale: string;
      breadcrumbs: { slug: string; name: string }[];
      article: {
        id: string; slug: string; category_id: string;
        title: string; excerpt: string | null; body_md: string;
        cover_image_url: string | null; author_name: string | null;
        read_minutes: number | null; published_at: string; updated_at: string;
        seo_title: string | null; seo_description: string | null;
        og_image_url: string | null;
      };
      related: ArticleSummary[];
      canonicalPathByLocale: Record<string, string>;
    };

/** Resolve /articles/... path segments to a category page or an article. */
export const getArticleByPath = createServerFn({ method: "GET" })
  .inputValidator((d: { path: string; locale?: string }) =>
    z.object({ path: z.string().min(1).max(500), locale: z.string().min(2).max(8).optional() }).parse(d),
  )
  .handler(async ({ data }): Promise<ArticlePathResult> => {
    const locale = data.locale ?? DEFAULT_LOCALE;
    const segments = data.path.split("/").filter(Boolean);
    if (segments.length === 0) return { type: "none" };

    try {
      const { data: allCats } = await sb()
        .from("article_categories")
        .select("id, slug, parent_id, icon, sort, is_published, article_category_translations(name, description, seo_title, seo_description, locale)")
        .eq("is_published", true);
      const cats: any[] = allCats ?? [];

      const path: any[] = [];
      let parentId: string | null = null;
      let consumed = 0;
      for (const seg of segments) {
        const match = cats.find((c: any) => c.parent_id === parentId && c.slug === seg);
        if (!match) break;
        path.push(match);
        parentId = match.id;
        consumed++;
      }

      const catBreadcrumbs = (chain: any[]): { slug: string; name: string }[] =>
        chain.map((c: any) => {
          const t = pickTr(c.article_category_translations, locale);
          return { slug: c.slug, name: t?.name ?? c.slug };
        });

      const canonicalPathByLocale = (chain: any[], articleSlug?: string): Record<string, string> => {
        const out: Record<string, string> = {};
        const segs = chain.map((c: any) => c.slug);
        for (const loc of ARTICLES_LOCALES) {
          const s = [...segs];
          if (articleSlug) s.push(articleSlug);
          out[loc] = buildArticlesHref(loc, s);
        }
        return out;
      };

      // Fully consumed → category page.
      if (consumed === segments.length && path.length > 0) {
        const leaf = path[path.length - 1];
        const tr = pickTr(leaf.article_category_translations, locale);

        // Descendant categories.
        const collectDescIds = (id: string): string[] => {
          const kids = cats.filter((c: any) => c.parent_id === id);
          return [id, ...kids.flatMap((k: any) => collectDescIds(k.id))];
        };
        const scopeIds = collectDescIds(leaf.id);

        const { data: arts } = await sb()
          .from("articles")
          .select("id, slug, category_id, cover_image_url, author_name, read_minutes, published_at, is_published, article_translations(title, excerpt, locale)")
          .eq("is_published", true)
          .in("category_id", scopeIds)
          .order("published_at", { ascending: false });
        const articles: ArticleSummary[] = (arts ?? []).map((a: any) => {
          const atr = pickTr(a.article_translations, locale);
          return {
            id: a.id, slug: a.slug, category_id: a.category_id,
            cover_image_url: a.cover_image_url, author_name: a.author_name,
            read_minutes: a.read_minutes, published_at: a.published_at,
            title: atr?.title ?? a.slug, excerpt: atr?.excerpt ?? null,
          };
        });

        const subcategories = cats
          .filter((c: any) => c.parent_id === leaf.id)
          .map((c: any) => {
            const ctr = pickTr(c.article_category_translations, locale);
            const descIds = new Set(collectDescIds(c.id));
            const count = articles.filter((a) => descIds.has(a.category_id)).length;
            return { id: c.id, slug: c.slug, name: ctr?.name ?? c.slug, description: ctr?.description ?? null, icon: c.icon, article_count: count };
          });

        return {
          type: "category" as const,
          locale,
          breadcrumbs: catBreadcrumbs(path),
          category: {
            id: leaf.id, slug: leaf.slug, name: tr?.name ?? leaf.slug,
            description: tr?.description ?? null, icon: leaf.icon,
            seo_title: tr?.seo_title ?? null, seo_description: tr?.seo_description ?? null,
          },
          subcategories,
          articles,
          canonicalPathByLocale: canonicalPathByLocale(path),
        };
      }

      // Otherwise, last segment might be an article slug under `path[last]`.
      if (path.length === 0) return { type: "none" };
      const articleSlug = segments[consumed];
      if (!articleSlug || consumed !== segments.length - 1) return { type: "none" };
      const leaf = path[path.length - 1];

      const { data: artRows } = await sb()
        .from("articles")
        .select("id, slug, category_id, cover_image_url, author_name, read_minutes, published_at, updated_at, is_published, article_translations(title, excerpt, body_md, seo_title, seo_description, og_image_url, locale)")
        .eq("category_id", leaf.id)
        .eq("slug", articleSlug)
        .eq("is_published", true)
        .limit(1);
      const art = (artRows ?? [])[0];
      if (!art) return { type: "none" };

      const atr = pickTr(art.article_translations, locale);

      // Related — up to 4 more articles in the same category.
      const { data: relRows } = await sb()
        .from("articles")
        .select("id, slug, category_id, cover_image_url, author_name, read_minutes, published_at, is_published, article_translations(title, excerpt, locale)")
        .eq("category_id", leaf.id)
        .eq("is_published", true)
        .neq("id", art.id)
        .order("published_at", { ascending: false })
        .limit(4);
      const related: ArticleSummary[] = (relRows ?? []).map((a: any) => {
        const rtr = pickTr(a.article_translations, locale);
        return {
          id: a.id, slug: a.slug, category_id: a.category_id,
          cover_image_url: a.cover_image_url, author_name: a.author_name,
          read_minutes: a.read_minutes, published_at: a.published_at,
          title: rtr?.title ?? a.slug, excerpt: rtr?.excerpt ?? null,
        };
      });

      return {
        type: "article" as const,
        locale,
        breadcrumbs: catBreadcrumbs(path),
        article: {
          id: art.id, slug: art.slug, category_id: art.category_id,
          title: atr?.title ?? art.slug,
          excerpt: atr?.excerpt ?? null,
          body_md: atr?.body_md ?? "",
          cover_image_url: art.cover_image_url,
          author_name: art.author_name,
          read_minutes: art.read_minutes,
          published_at: art.published_at,
          updated_at: art.updated_at,
          seo_title: atr?.seo_title ?? null,
          seo_description: atr?.seo_description ?? null,
          og_image_url: atr?.og_image_url ?? null,
        },
        related,
        canonicalPathByLocale: canonicalPathByLocale(path, art.slug),
      };
    } catch (e) {
      console.error("[articles] getArticleByPath failed", e);
      return { type: "none" };
    }
  });

/** All articles URLs (per locale) for the sitemap. */
export const getAllArticleUrls = createServerFn({ method: "GET" })
  .handler(async () => {
    try {
      const [{ data: cats }, { data: arts }] = await Promise.all([
        sb().from("article_categories")
          .select("id, slug, parent_id, is_published, article_category_translations(locale)")
          .eq("is_published", true),
        sb().from("articles")
          .select("id, slug, category_id, updated_at, published_at, is_published, article_translations(locale)")
          .eq("is_published", true),
      ]);

      const catList: any[] = cats ?? [];
      const catById = new Map<string, any>(catList.map((c) => [c.id, c]));
      const childrenOf = (parentId: string | null) => catList.filter((c) => c.parent_id === parentId);
      const chainOf = (catId: string): any[] => {
        const chain: any[] = [];
        let cur = catById.get(catId);
        while (cur) { chain.unshift(cur); cur = cur.parent_id ? catById.get(cur.parent_id) : undefined; }
        return chain;
      };

      const articleCountByCat = new Map<string, number>();
      for (const a of (arts ?? [])) articleCountByCat.set(a.category_id, (articleCountByCat.get(a.category_id) ?? 0) + 1);
      const countWithDescendants = (catId: string): number => {
        let count = articleCountByCat.get(catId) ?? 0;
        for (const child of childrenOf(catId)) count += countWithDescendants(child.id);
        return count;
      };

      type Entry = { kind: "category" | "article"; updated_at?: string; pathByLocale: Record<string, string> };
      const out: Entry[] = [];

      for (const c of catList) {
        if (countWithDescendants(c.id) === 0) continue;
        const chain = chainOf(c.id);
        const pathByLocale: Record<string, string> = {};
        for (const loc of ARTICLES_LOCALES) {
          pathByLocale[loc] = buildArticlesHref(loc, chain.map((cc) => cc.slug));
        }
        out.push({ kind: "category", pathByLocale });
      }

      for (const a of (arts ?? [])) {
        const chain = chainOf(a.category_id);
        if (chain.length === 0) continue;
        const pathByLocale: Record<string, string> = {};
        for (const loc of ARTICLES_LOCALES) {
          pathByLocale[loc] = buildArticlesHref(loc, [...chain.map((cc) => cc.slug), a.slug]);
        }
        out.push({ kind: "article", updated_at: a.updated_at, pathByLocale });
      }

      return { entries: out };
    } catch (e) {
      console.error("[articles] getAllArticleUrls failed", e);
      return { entries: [] as Array<{ kind: "category" | "article"; updated_at?: string; pathByLocale: Record<string, string> }> };
    }
  });